import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { AUTO_STYLE, keyframes, style } from '@angular/animations';

import { NgStaticAnimationService } from '../../shared/services/static-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function skewForward(skew: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	skew = skew ? skew : 10;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility				: AUTO_STYLE,
				transform				: `skew(-${skew}deg)`,
				'transition-property'	: 'transform',
				'transform-origin'		: '0 100%',
				easing					: 'ease',
				offset					: 0
			}),
			style({
				transform				: 'skew(0)',
				'transition-property'	: 'transform',
				'transform-origin'		: '0 100%',
				easing					: 'ease',
				offset					: 1
			})
		]),
		end		: keyframes([
			style({
				visibility				: AUTO_STYLE,
				transform				: 'skew(0)',
				'transition-property'	: 'transform',
				'transform-origin'		: '0 100%',
				easing					: 'ease',
				offset					: 0
			}),
			style({
				transform				: `skew(-${skew}deg)`,
				'transition-property'	: 'transform',
				'transform-origin'		: '0 100%',
				easing					: 'ease',
				offset					: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Skew Forward on Event
 */
@Directive({
    selector: '[ngSkewForwardOnEvent]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgSkewForwardOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement					  = null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the skew value is relative to the animation functionality
		if (this.effectOptions?.skew && this.effectOptions?.skew < 1) throw new Error(`SkewForwardOnEventDirective => ngOnInit => FATAL ERROR: Provided skew value should be positive number: ${this.effectOptions?.skew}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: skewForward(this.effectOptions?.skew),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * SkewForward on Mouse Hover
 */
@Directive({
    selector: '[ngSkewForwardOnMouseHover]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgSkewForwardOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement 					= null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the skew value is relative to the animation functionality
		if (this.timingOptions?.skew && this.timingOptions?.skew < 1) throw new Error(`SkewForwardOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided skew value should be positive number: ${this.timingOptions?.skew}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: skewForward(this.timingOptions?.skew),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	skew : number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	skew : number;
}
